/**
 * The careers page's live openings (owner, 2026-10-19): openings are published in the Admin (Configurações › Vagas do
 * site, scoped to a country and optionally a city) and read here from the public route
 * `GET /v1/public/career-openings?country=XX` (CORS-open, no login). Pure: the request URL and the mapping of the answer
 * to what the page shows. Anything malformed is dropped; a failed read means "no openings" (the page keeps its text).
 * Neutral: no country, city or language is named here.
 */
import { resolveSiteTarget } from './site-links.js';

export interface CareerOpeningView {
  readonly openingId: string;
  readonly title: string;
  readonly team: string;
  readonly employmentType: string;
  readonly summary: string;
  readonly description: string;
  /** The city's name, or undefined for an opening that covers the whole country. */
  readonly marketName?: string;
  /** An https or mailto address to apply; undefined → apply through the Central chat. */
  readonly applyUrl?: string;
  readonly openedAt?: string;
}

const COUNTRY = /^[A-Z]{2}$/;
const str = (v: unknown, max: number): string | undefined => (typeof v === 'string' && v.trim() !== '' && v.length <= max ? v.trim() : undefined);

/** The public openings URL for a country, or undefined when the country is not a well-formed code. */
export function careerOpeningsUrl(apiOrigin: string, country: string): string | undefined {
  const cc = country.toUpperCase();
  if (!COUNTRY.test(cc)) return undefined;
  return `${apiOrigin.replace(/\/+$/, '')}/v1/public/career-openings?country=${cc}`;
}

/** The openings the page shows from the API's answer (status + JSON body); [] for anything else. */
export function careerOpeningsOf(status: number, body: unknown): readonly CareerOpeningView[] {
  if (status !== 200 || typeof body !== 'object' || body === null) return [];
  const list = (body as { openings?: unknown }).openings;
  if (!Array.isArray(list)) return [];
  const out: CareerOpeningView[] = [];
  for (const raw of list) {
    if (typeof raw !== 'object' || raw === null) continue;
    const o = raw as Record<string, unknown>;
    const openingId = str(o.openingId, 64);
    const title = str(o.title, 120);
    const team = str(o.team, 60);
    const employmentType = str(o.employmentType, 40);
    const summary = str(o.summary, 280);
    const description = str(o.description, 5000);
    if (openingId === undefined || title === undefined || team === undefined || employmentType === undefined || summary === undefined || description === undefined) continue;
    const marketName = str(o.marketName, 120);
    const apply = str(o.applyUrl, 2000);
    const applyUrl = apply !== undefined && resolveSiteTarget(apply).kind === 'external' ? apply : undefined;
    const openedAt = str(o.openedAt, 40);
    out.push({ openingId, title, team, employmentType, summary, description, ...(marketName !== undefined ? { marketName } : {}), ...(applyUrl !== undefined ? { applyUrl } : {}), ...(openedAt !== undefined ? { openedAt } : {}) });
  }
  return out;
}
